import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import { dsV2 } from '@bloomays-lib/ui.shared';
import type { ActivityDayDetails } from '@bloomays-lib/types.shared';
import { formatCraDays } from '../../../helpers/craDashboard';

/**
 * Grille calendaire d'un mois de CRA (composant autonome, testé via story).
 *
 * 7 colonnes lundi→dimanche, pastille « aujourd'hui ». `dayWorked` est une **fraction de journée**
 * (1 = jour plein, 0.5 = demi, 0.875 = 7h/8h…). Couleurs : présent (vert) > 0.5 j, demi-journée
 * (ambre) > 0, **HNO (rouge clair)** pour une journée travaillée un jour férié ou un week-end,
 * absent (gris) sinon. Un week-end non travaillé est masqué (alignement lun→dim préservé).
 *
 * Deux niveaux de lecture :
 * - `compact` (défaut) — pastilles denses du tableau de bord mission ;
 * - `detailed` — en-tête lun→dim, quantité déclarée dans la case, marqueur de commentaire,
 *   week-ends non travaillés visibles et journées sélectionnables : lecture de contrôle d'un CRA
 *   (vérification face à la facture fournisseur).
 */
export type CraMonthGridVariant = 'compact' | 'detailed';

export type CraMonthGridProps = {
  days: ActivityDayDetails[];
  /** Date de référence pour la pastille « aujourd'hui » (injectable pour les tests/stories). */
  referenceDate?: Date;
  /** Niveau de lecture de la grille (cf. doc du composant). Défaut : `compact`. */
  variant?: CraMonthGridVariant;
  /** Journée mise en évidence (numéro du jour dans le mois) — variante `detailed`. */
  selectedDayId?: number;
  /** Rend les journées cliquables pour inspecter le détail d'une journée. */
  onSelectDay?: (day: ActivityDayDetails) => void;
};

const WEEKDAYS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

/** Quantité déclarée en français, sans arrondi de lecture (« 1 j », « 0,5 j », « 0,375 j »). */
const formatDayAmount = (value: number): string => `${formatCraDays(value)} j`;

/** Quantité travaillée affichée dans la case (variante `detailed`) — vide si rien de déclaré. */
export const craDayAmountLabel = (day: Pick<ActivityDayDetails, 'dayWorked'>): string => {
  const worked = day.dayWorked ?? 0;
  return worked > 0 ? formatDayAmount(worked) : '';
};

/**
 * Marqueur d'astreinte (HNO) d'une journée — affiché en plus de la quantité travaillée, pour
 * qu'une astreinte posée sur un jour ouvré travaillé se voie sans survoler la case.
 */
export const craDayAstreinteLabel = (day: Pick<ActivityDayDetails, 'astreinte'>): string => {
  const astreinte = day.astreinte ?? 0;
  return astreinte > 0 ? `astr. ${formatDayAmount(astreinte)}` : '';
};

/** Détail complet d'une journée, exposé au survol (quantité, astreinte, commentaire). */
export const craDayTitle = (day: ActivityDayDetails): string =>
  [
    day.date,
    day.isHoliday ? 'férié' : '',
    formatDayAmount(day.dayWorked ?? 0),
    (day.astreinte ?? 0) > 0 ? `astreinte ${formatDayAmount(day.astreinte ?? 0)}` : '',
    day.comment ? `« ${day.comment} »` : '',
  ]
    .filter(Boolean)
    .join(' · ');

const CRA_DAY_TONE = {
  full: { bg: dsV2.color.vertDim, fg: dsV2.color.vert, brd: dsV2.color.vertBrd },
  half: { bg: dsV2.color.amberDim, fg: dsV2.color.amber, brd: dsV2.color.amberBrd },
  hno: { bg: dsV2.color.rougeLight, fg: dsV2.color.rougeDark, brd: dsV2.color.rougeBorder },
  off: { bg: dsV2.color.surface2, fg: dsV2.color.t4, brd: dsV2.color.borderLight },
} as const;

/** Colonne (lundi = 0) à partir d'une date `dd/MM/yyyy`. */
const dayColumn = (date: string): number => {
  const [dd, mm, yyyy] = date.split('/').map(Number);
  return (new Date(yyyy, (mm ?? 1) - 1, dd).getDay() + 6) % 7;
};

const isSameDay = (date: string, reference: Date): boolean => {
  const [dd, mm, yyyy] = date.split('/').map(Number);
  return reference.getDate() === dd && reference.getMonth() + 1 === mm && reference.getFullYear() === yyyy;
};

export type CraDayKind = keyof typeof CRA_DAY_TONE | 'hidden';

/**
 * Tonalité d'un jour de CRA :
 * - HNO (rouge clair) si une journée travaillée tombe un jour férié (`isHoliday`, méthode existante)
 *   ou un week-end ;
 * - présent (vert) > 0.5 j, demi-journée (ambre) > 0, absent (gris) sinon ;
 * - week-end non travaillé → masqué (`hidden`) en lecture `compact`, pour préserver l'alignement
 *   lun→dim ; visible en gris en lecture `detailed`, où l'on contrôle le mois jour par jour.
 */
export const craDayTone = (
  day: Pick<ActivityDayDetails, 'dayWorked' | 'astreinte' | 'isSaturday' | 'isSunday' | 'isHoliday'>,
  variant: CraMonthGridVariant = 'compact',
): CraDayKind => {
  const worked = day.dayWorked ?? 0;
  const weekend = day.isSaturday || day.isSunday;
  if (worked > 0 && (weekend || day.isHoliday)) return 'hno';
  // Astreinte seule : du temps déclaré hors heures ouvrées, signalé comme tel en lecture de
  // contrôle (la lecture compacte du tableau de bord mission ne montre que la présence).
  if (variant === 'detailed' && worked === 0 && (day.astreinte ?? 0) > 0) return 'hno';
  if (weekend) return variant === 'detailed' ? 'off' : 'hidden';
  if (worked > 0.5) return 'full';
  if (worked > 0) return 'half';
  return 'off';
};

const LEGEND: { label: string; tone: keyof typeof CRA_DAY_TONE }[] = [
  { label: 'Présent', tone: 'full' },
  { label: '½ journée', tone: 'half' },
  { label: 'HNO', tone: 'hno' },
  { label: 'Absent', tone: 'off' },
];

export const CraMonthGrid = ({
  days,
  referenceDate,
  variant = 'compact',
  selectedDayId,
  onSelectDay,
}: CraMonthGridProps) => {
  if (!days || days.length === 0) return null;
  const reference = referenceDate ?? new Date();
  const detailed = variant === 'detailed';
  const cells: (ActivityDayDetails | null)[] = [...Array(dayColumn(days[0].date)).fill(null), ...days];

  return (
    <Box>
      {detailed && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginBottom: '6px' }}>
          {WEEKDAYS.map((weekday) => (
            <Box
              key={weekday}
              sx={{
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: dsV2.color.t4,
              }}
            >
              {weekday}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginBottom: '11px' }}>
        {cells.map((d, i) => {
          if (!d) return <Box key={`empty-${i}`} />;
          const kind = craDayTone(d, variant);
          // Week-end non travaillé masqué (préserve l'alignement lun→dim) ; un week-end ou férié
          // travaillé reste affiché, en HNO.
          if (kind === 'hidden') return <Box key={d.id} sx={{ aspectRatio: '1', opacity: 0 }} />;
          const tone = CRA_DAY_TONE[kind];
          const selected = detailed && selectedDayId === d.id;
          return (
            <Box
              key={d.id}
              component={onSelectDay ? ButtonBase : 'div'}
              {...(onSelectDay
                ? { onClick: () => onSelectDay(d), 'aria-label': craDayTitle(d), 'aria-pressed': selected }
                : {})}
              title={detailed ? craDayTitle(d) : undefined}
              sx={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: detailed ? '1px' : 0,
                fontSize: detailed ? '11.5px' : '9.5px',
                fontFamily: dsV2.font.sansMedium,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: detailed ? 600 : 500,
                background: tone.bg,
                color: tone.fg,
                border: `1px solid ${tone.brd}`,
                ...(selected ? { border: `2px solid ${dsV2.color.noir}` } : {}),
                ...(isSameDay(d.date, reference)
                  ? { outline: `2px solid ${dsV2.color.bleu}`, outlineOffset: '1px' }
                  : {}),
              }}
            >
              {d.id}
              {detailed && craDayAmountLabel(d) ? (
                <Box component="span" sx={{ fontSize: '9px', fontWeight: 500, opacity: 0.85 }}>
                  {craDayAmountLabel(d)}
                </Box>
              ) : null}
              {/* Astreinte : visible même sur un jour ouvré travaillé, où la teinte de la case
                  reste celle de la présence. */}
              {detailed && craDayAstreinteLabel(d) ? (
                <Box
                  component="span"
                  sx={{ fontSize: '8.5px', fontWeight: 600, color: CRA_DAY_TONE.hno.fg, whiteSpace: 'nowrap' }}
                >
                  {craDayAstreinteLabel(d)}
                </Box>
              ) : null}
              {detailed && d.comment ? (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: '3px',
                    right: '3px',
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'currentColor',
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
      <Stack direction="row" spacing="12px" sx={{ flexWrap: 'wrap' }}>
        {LEGEND.map((leg) => {
          const tone = CRA_DAY_TONE[leg.tone];
          return (
            <Stack
              key={leg.label}
              direction="row"
              spacing="5px"
              alignItems="center"
              sx={{ fontSize: 11, color: dsV2.color.t4 }}
            >
              <Box
                sx={{ width: 9, height: 9, borderRadius: '3px', background: tone.bg, border: `1px solid ${tone.brd}` }}
              />
              {leg.label}
            </Stack>
          );
        })}
        {detailed && (
          <Stack direction="row" spacing="5px" alignItems="center" sx={{ fontSize: 11, color: dsV2.color.t4 }}>
            <Box component="span" sx={{ fontSize: 9, fontWeight: 600, color: CRA_DAY_TONE.hno.fg }}>
              astr.
            </Box>
            Astreinte (HNO)
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CraMonthGrid;
